import { useEffect, useState } from "react";
import { NavLink, Navigate, Route, Routes } from "react-router-dom";
import { fetchMe, type Me } from "./lib/api";
import { firstName } from "./lib/format";
import { timeOfDayGreeting, approximateLocationAndZone } from "./lib/greeting";
import Dashboard from "./pages/Dashboard";
import Approvals from "./pages/Approvals";
import Executive from "./pages/Executive";
import Admin from "./pages/Admin";
import TeamCalendar from "./pages/TeamCalendar";
import Help from "./pages/Help";
import { RISE_APP_URL } from "./lib/shared/riseLink";

type AppState =
  | { status: "loading" }
  | { status: "signed-out" }
  | { status: "signed-in"; me: Me }
  | { status: "ineligible"; me: Me }
  | { status: "error"; message: string };

export default function App() {
  const [state, setState] = useState<AppState>({ status: "loading" });

  useEffect(() => {
    fetchMe()
      .then((result) => {
        if (result === "unauthenticated") {
          setState({ status: "signed-out" });
        } else if (!result.isEligibleRequester && !result.isApprover && !result.isExecutive) {
          setState({ status: "ineligible", me: result });
        } else {
          setState({ status: "signed-in", me: result });
        }
      })
      .catch((err) => setState({ status: "error", message: err.message }));
  }, []);

  if (state.status === "loading") {
    return (
      <div className="app-shell">
        <Topbar />
        <div className="centered-screen">
          <p className="di-p">Loading…</p>
        </div>
      </div>
    );
  }

  if (state.status === "signed-out") {
    return (
      <div className="login-screen">
        <div className="login-card">
          <h1 className="di-h2">Sign in</h1>
          <p className="di-p">Sign in with your Dispatch Google account to continue.</p>
          <a className="di-btn di-btn-primary" href="/auth/login">
            Sign in with Google
          </a>
        </div>
      </div>
    );
  }

  if (state.status === "ineligible") {
    return (
      <div className="app-shell">
        <Topbar name={state.me.name} />
        <div className="centered-screen">
          {state.me.inRise ? (
            <div className="centered-card rise-card">
              <div className="rise-icon">i</div>
              <h1 className="di-h2">You're all set in Rise, {firstName(state.me.name)}</h1>
              <p className="di-p">
                This tool is just for contractors and global staff who aren't on Rise. Because you're
                using <b>Rise</b> as your HR system, your vacation, sick and personal time are all
                booked and tracked there.
              </p>
              <p className="di-small">Request time off there and it routes to your manager for approval.</p>
              <a className="di-btn di-btn-primary" href={RISE_APP_URL} target="_blank" rel="noreferrer">
                Open Rise →
              </a>
            </div>
          ) : (
            <div className="centered-card">
              <h1 className="di-h2">Nothing to do here yet</h1>
              <p className="di-p">
                Dispatch Time Off is for contractors and global staff who aren't on Rise. Your account
                isn't currently set up to submit or approve requests here -- if that's unexpected,
                check with an admin.
              </p>
            </div>
          )}
        </div>
      </div>
    );
  }

  if (state.status === "error") {
    return (
      <div className="app-shell">
        <Topbar />
        <div className="centered-screen">
          <div className="centered-card">
            <p className="di-p">Something went wrong: {state.message}</p>
          </div>
        </div>
      </div>
    );
  }

  const { me } = state;
  const defaultPath = me.isEligibleRequester ? "/" : me.isApprover ? "/approvals" : me.isExecutive ? "/org" : "/calendar";

  return (
    <div className="app-shell">
      <Topbar name={me.name} />
      <div className="greeting-banner">
        <p className="di-eyebrow">{approximateLocationAndZone()}</p>
        <h1 className="di-h3">
          {timeOfDayGreeting()}, {firstName(me.name)}
        </h1>
      </div>
      <nav className="nav-tabs">
        {me.isEligibleRequester && (
          <NavLink to="/" end className={({ isActive }) => (isActive ? "nav-tab nav-tab-active" : "nav-tab")}>
            My requests
          </NavLink>
        )}
        {me.isApprover && (
          <NavLink to="/approvals" className={({ isActive }) => (isActive ? "nav-tab nav-tab-active" : "nav-tab")}>
            Acknowledgements
          </NavLink>
        )}
        <NavLink to="/calendar" className={({ isActive }) => (isActive ? "nav-tab nav-tab-active" : "nav-tab")}>
          Team calendar
        </NavLink>
        {me.isExecutive && (
          <NavLink to="/org" className={({ isActive }) => (isActive ? "nav-tab nav-tab-active" : "nav-tab")}>
            Org-wide
          </NavLink>
        )}
        {me.isExecutive && (
          <NavLink to="/admin" className={({ isActive }) => (isActive ? "nav-tab nav-tab-active" : "nav-tab")}>
            Admin
          </NavLink>
        )}
        <NavLink to="/help" className={({ isActive }) => (isActive ? "nav-tab nav-tab-active" : "nav-tab")}>
          Help
        </NavLink>
      </nav>

      <main className="page-main">
        <Routes>
          {me.isEligibleRequester && <Route path="/" element={<Dashboard me={me} />} />}
          {me.isApprover && <Route path="/approvals" element={<Approvals />} />}
          <Route path="/calendar" element={<TeamCalendar />} />
          {me.isExecutive && <Route path="/org" element={<Executive />} />}
          {me.isExecutive && <Route path="/admin" element={<Admin />} />}
          <Route path="/help" element={<Help />} />
          <Route path="*" element={<Navigate to={defaultPath} replace />} />
        </Routes>
      </main>
    </div>
  );
}

function Topbar({ name }: { name?: string }) {
  return (
    <header className="topbar">
      <div className="brand-name">
        Dispatch <b>Time Off</b>
      </div>
      {name && (
        <div className="topbar-user">
          <span className="di-small">{firstName(name)}</span>
          <button
            type="button"
            className="topbar-signout"
            onClick={() => {
              fetch("/auth/logout", { method: "POST", credentials: "include" }).finally(() => {
                window.location.href = "/";
              });
            }}
          >
            Sign out
          </button>
        </div>
      )}
    </header>
  );
}
